// src/pages/Shop.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { Search, SlidersHorizontal, Package } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const productsQuery = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const productsSnap = await getDocs(productsQuery);
        const productsData = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(productsData);
        setFilteredProducts(productsData);

        const catSnap = await getDocs(collection(db, 'categories'));
        setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    let result = [...products];

    if (searchTerm) {
      result = result.filter(p =>
        p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    if (sortBy === 'low-high') result.sort((a, b) => (a.price || 0) - (b.price || 0));
    else if (sortBy === 'high-low') result.sort((a, b) => (b.price || 0) - (a.price || 0));
    else if (sortBy === 'popular') result.sort((a, b) => (b.reviews || 0) - (a.reviews || 0));
    else if (sortBy === 'newest') result.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

    setFilteredProducts(result);
  }, [searchTerm, sortBy, selectedCategory, products]);

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)', color: 'var(--color-primary-text)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        <div className="text-center mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: 'var(--color-primary-text)' }}>
            All Products
          </h1>
          <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
            {filteredProducts.length} products available
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-xl shadow-md p-4 mb-6 flex flex-col md:flex-row gap-3"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
          <div className="relative flex-1">
            <input type="text" placeholder="Search products..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg py-3 px-4 pl-11 text-sm focus:outline-none"
              style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }} />
            <Search className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
          </div>

          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
            className="rounded-lg py-3 px-3 text-sm focus:outline-none cursor-pointer"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}>
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            className="rounded-lg py-3 px-3 text-sm focus:outline-none cursor-pointer"
            style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}>
            <option value="newest">Newest First</option>
            <option value="low-high">Price: Low to High</option>
            <option value="high-low">Price: High to Low</option>
            <option value="popular">Most Popular</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
              style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
            <p className="mt-4" style={{ color: 'var(--color-secondary-text)' }}>Loading...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package size={64} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
            <p className="text-lg" style={{ color: 'var(--color-secondary-text)' }}>No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product, i) => (
              <div key={product.id} className="anim-fadeInUp" style={{ animationDelay: `${i * 50}ms` }}>
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        )}

        <div className="h-20 md:h-0"></div>
      </div>
    </div>
  );
};

export default Shop;